import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { ContractEditor } from '../components/ContractEditor';
import { GatewayLogsSection } from '../components/GatewayLogsSection';
import { 
  Settings, ToggleLeft, FileText, Globe, Search, Package, Database, Key, 
  Activity, ChevronRight, Save, Plus, Trash2, Download, Upload, RefreshCw,
  Building2, Phone, Mail, Clock, ExternalLink, Check, X, Edit, MapPin, Send,
  CreditCard, Eye, EyeOff, ScrollText
} from 'lucide-react';

const MENU_ITEMS = [
  { id: 'general', label: 'General Settings', icon: Settings, description: 'Site configuration' },
  { id: 'features', label: 'Features', icon: ToggleLeft, description: 'Toggle modules on/off' },
  { id: 'email', label: 'Email Settings', icon: Mail, description: 'SMTP configuration' },
  { id: 'payments', label: 'Payment Settings', icon: CreditCard, description: 'Stripe configuration' },
  { id: 'gateway-logs', label: 'Gateway Transaction Log', icon: FileText, description: 'Payment gateway logs' },
  { id: 'rules', label: 'Site Rules', icon: FileText, description: 'Terms & policies' },
  { id: 'pages', label: 'Page Generator', icon: Globe, description: 'Simple SEO pages' },
  { id: 'locations', label: 'Location Pages', icon: MapPin, description: 'One-click location pages' },
  { id: 'directories', label: 'Directory Submissions', icon: Send, description: 'SEO business listings' },
  { id: 'seo', label: 'SEO Tools', icon: Search, description: 'Sitemap submission' },
  { id: 'products', label: 'Products', icon: Package, description: 'Product catalog' },
  { id: 'database', label: 'Database', icon: Database, description: 'Backup & restore' },
  { id: 'api', label: 'API Tools', icon: Key, description: 'API key management', future: true },
  { id: 'logs', label: 'System Logs', icon: Activity, description: 'Activity logs', future: true },
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

const FEATURES_LIST = [
  { id: 'organic_seo', name: 'Organic SEO', description: 'SEO location pages visible on public site', category: 'Public Site' },
  { id: 'patient_crm', name: 'Patient Portal / CRM', description: 'Patient CRM section visible on public site', category: 'Public Site' },
  { id: 'order_processing', name: 'Order Processing', description: 'Order processing section visible on public site', category: 'Public Site' },
  { id: 'leads', name: 'Lead Management', description: 'Kanban board and lead tracking', category: 'CRM Modules' },
  { id: 'projects', name: 'Project Management', description: 'Project tracking and costs', category: 'CRM Modules' },
  { id: 'tasks', name: 'Task Management', description: 'Task assignment and tracking', category: 'CRM Modules' },
  { id: 'invoices', name: 'Invoicing', description: 'Invoice generation and management', category: 'CRM Modules' },
  { id: 'bookings', name: 'Booking Scheduler', description: 'Appointment scheduling', category: 'CRM Modules' },
  { id: 'signatures', name: 'E-Signatures', description: 'Digital signature capture', category: 'CRM Modules' },
  { id: 'products', name: 'Product Catalog', description: 'Product management', category: 'CRM Modules' },
  { id: 'newsletter', name: 'Newsletter', description: 'Email newsletter signup', category: 'CRM Modules' },
  { id: 'demo_requests', name: 'Demo Requests', description: 'Demo request form', category: 'CRM Modules' },
  { id: 'contact_form', name: 'Contact Form', description: 'Website contact form', category: 'CRM Modules' },
];

const RULE_CATEGORIES = [
  { id: 'terms', name: 'Terms of Service' },
  { id: 'privacy', name: 'Privacy Policy' },
  { id: 'refund', name: 'Refund Policy' },
  { id: 'shipping', name: 'Shipping Policy' },
  { id: 'hipaa', name: 'HIPAA Compliance' },
  { id: 'accessibility', name: 'Accessibility Statement' },
];

const SidebarMenuItem = ({ item, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
      active 
        ? 'bg-[#014DB7] text-white' 
        : item.future 
          ? 'text-gray-400 hover:bg-gray-100 cursor-not-allowed'
          : 'text-gray-600 hover:bg-gray-100'
    }`}
    disabled={item.future}
  >
    <item.icon className="w-5 h-5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm">{item.label}</p>
      <p className={`text-xs truncate ${active ? 'text-blue-100' : 'text-gray-400'}`}>
        {item.description}
      </p>
    </div>
    {item.future && (
      <span className="text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">Soon</span>
    )}
    {!item.future && <ChevronRight className={`w-4 h-4 ${active ? 'text-white' : 'text-gray-300'}`} />}
  </button>
);

// General Settings Section
const GeneralSettings = ({ api }) => {
  const [settings, setSettings] = useState({
    site_name: 'DME R\'US',
    tagline: 'HIPAA-Compliant DME Management Platform',
    contact_email: 'info@dmerus.com',
    contact_phone: '(941) 466-4-DME',
    address: '11161 E State Rd 70, Ste 110-556, Lakewood Ranch, FL 34202',
    city: 'Los Angeles',
    state: 'California',
    zip: '90001',
    business_hours_start: '09:00',
    business_hours_end: '17:00',
    timezone: 'America/Los_Angeles',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/api/settings/general');
        if (res.data) setSettings(prev => ({ ...prev, ...res.data }));
      } catch (error) {
        console.log('Using default settings');
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/settings/general', settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">General Settings</h2>
          <p className="text-gray-500">Configure your site's basic information</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#014DB7] text-white rounded-xl">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Site Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#014DB7]" /> Site Information
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Site Name</Label>
              <Input value={settings.site_name} onChange={(e) => setSettings({...settings, site_name: e.target.value})} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input value={settings.tagline} onChange={(e) => setSettings({...settings, tagline: e.target.value})} className="rounded-xl" />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#84CC16]" /> Contact Information
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={settings.contact_email} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={settings.contact_phone} onChange={(e) => setSettings({...settings, contact_phone: e.target.value})} className="rounded-xl" />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-red-500" /> Business Address
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Street Address</Label>
              <Input value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} className="rounded-xl" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={settings.city} onChange={(e) => setSettings({...settings, city: e.target.value})} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={settings.state} onValueChange={(v) => setSettings({...settings, state: v})}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ZIP</Label>
                <Input value={settings.zip} onChange={(e) => setSettings({...settings, zip: e.target.value})} className="rounded-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Business Hours */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-500" /> Business Hours
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Opening Time</Label>
                <Input type="time" value={settings.business_hours_start} onChange={(e) => setSettings({...settings, business_hours_start: e.target.value})} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Closing Time</Label>
                <Input type="time" value={settings.business_hours_end} onChange={(e) => setSettings({...settings, business_hours_end: e.target.value})} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={settings.timezone} onValueChange={(v) => setSettings({...settings, timezone: v})}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Features Section
const FeaturesSection = ({ api }) => {
  const [features, setFeatures] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await api.get('/api/settings/features');
        setFeatures(res.data || {});
      } catch (error) {
        // Initialize with all features enabled
        const defaultFeatures = {};
        FEATURES_LIST.forEach(f => defaultFeatures[f.id] = true);
        setFeatures(defaultFeatures);
      }
    };
    fetchFeatures();
  }, []);

  const handleToggle = async (featureId, enabled) => {
    setFeatures(prev => ({ ...prev, [featureId]: enabled }));
    try {
      await api.post('/api/settings/features', { ...features, [featureId]: enabled });
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${FEATURES_LIST.find(f => f.id === featureId)?.name}`);
    } catch (error) {
      toast.error('Failed to update feature');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#0A1628]">Feature Management</h2>
        <p className="text-gray-500">Enable or disable application modules. Public Site toggles control what visitors see on the marketing website.</p>
      </div>

      {['Public Site', 'CRM Modules'].map(category => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{category}</h3>
          <div className="grid grid-cols-2 gap-4">
            {FEATURES_LIST.filter(f => f.category === category).map(feature => (
              <div key={feature.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${features[feature.id] !== false ? 'bg-[#014DB7]/10' : 'bg-gray-100'}`}>
                    <ToggleLeft className={`w-5 h-5 ${features[feature.id] !== false ? 'text-[#014DB7]' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-[#0A1628]">{feature.name}</p>
                    <p className="text-xs text-gray-500">{feature.description}</p>
                  </div>
                </div>
                <Switch 
                  checked={features[feature.id] !== false} 
                  onCheckedChange={(checked) => handleToggle(feature.id, checked)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Payment Settings Section
const PaymentSettingsSection = ({ api }) => {
  var settingsState = useState({
    stripe_enabled: false,
    stripe_public_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: ''
  });
  var settings = settingsState[0];
  var setSettings = settingsState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  var showSecretState = useState(false);
  var showSecret = showSecretState[0];
  var setShowSecret = showSecretState[1];
  var showWebhookState = useState(false);
  var showWebhook = showWebhookState[0];
  var setShowWebhook = showWebhookState[1];

  useEffect(function() {
    api.get('/api/settings/payments').then(function(res) {
      if (res.data) {
        setSettings({
          stripe_enabled: res.data.stripe_enabled || false,
          stripe_public_key: res.data.stripe_public_key || '',
          stripe_secret_key: res.data.stripe_secret_key || '',
          stripe_webhook_secret: res.data.stripe_webhook_secret || ''
        });
      }
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }, []);

  function handleChange(field, value) {
    var updated = {};
    for (var key in settings) { updated[key] = settings[key]; }
    updated[field] = value;
    setSettings(updated);
  }

  function handleSave() {
    setSaving(true);
    api.post('/api/settings/payments', settings).then(function() {
      toast.success('Payment settings saved');
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to save settings');
      setSaving(false);
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Payment Settings</h2>
          <p className="text-gray-500 text-sm mt-1">Configure Stripe for payment processing</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#014DB7]">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      {/* Stripe Enable Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Enable Stripe Payments</h3>
              <p className="text-sm text-gray-500">Accept credit card payments from clients</p>
            </div>
          </div>
          <Switch
            checked={settings.stripe_enabled}
            onCheckedChange={function(checked) { handleChange('stripe_enabled', checked); }}
          />
        </div>
      </div>

      {/* API Keys */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">Stripe API Keys</h3>
          <p className="text-sm text-gray-500">Get your keys from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#014DB7] hover:underline">Stripe Dashboard</a></p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-gray-700">Publishable Key</Label>
            <Input
              value={settings.stripe_public_key}
              onChange={function(e) { handleChange('stripe_public_key', e.target.value); }}
              placeholder="pk_live_..."
              className="mt-1 font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Starts with pk_test_ or pk_live_</p>
          </div>

          <div>
            <Label className="text-gray-700">Secret Key</Label>
            <div className="relative mt-1">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={settings.stripe_secret_key}
                onChange={function(e) { handleChange('stripe_secret_key', e.target.value); }}
                placeholder="sk_live_..."
                className="font-mono text-sm pr-10"
              />
              <button 
                type="button"
                onClick={function() { setShowSecret(!showSecret); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Starts with sk_test_ or sk_live_ - Keep this secret!</p>
          </div>

          <div>
            <Label className="text-gray-700">Webhook Secret</Label>
            <div className="relative mt-1">
              <Input
                type={showWebhook ? 'text' : 'password'}
                value={settings.stripe_webhook_secret}
                onChange={function(e) { handleChange('stripe_webhook_secret', e.target.value); }}
                placeholder="whsec_..."
                className="font-mono text-sm pr-10"
              />
              <button 
                type="button"
                onClick={function() { setShowWebhook(!showWebhook); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">For webhook event verification</p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">Setup Instructions</h4>
        <ol className="text-sm text-blue-800 space-y-2">
          <li>1. Create a <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="underline">Stripe account</a> if you don't have one</li>
          <li>2. Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Developers → API keys</a> and copy your keys</li>
          <li>3. For webhooks, go to <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="underline">Developers → Webhooks</a> and add endpoint</li>
          <li>4. Use test keys (pk_test_, sk_test_) for development</li>
        </ol>
      </div>

      {/* Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Payments Enabled</span>
            <span className={"px-2 py-1 rounded text-xs font-medium " + (settings.stripe_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600")}>
              {settings.stripe_enabled ? 'Active' : 'Disabled'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Public Key</span>
            <span className={"px-2 py-1 rounded text-xs font-medium " + (settings.stripe_public_key ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700")}>
              {settings.stripe_public_key ? 'Configured' : 'Not Set'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Secret Key</span>
            <span className={"px-2 py-1 rounded text-xs font-medium " + (settings.stripe_secret_key ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700")}>
              {settings.stripe_secret_key ? 'Configured' : 'Not Set'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Webhook Secret</span>
            <span className={"px-2 py-1 rounded text-xs font-medium " + (settings.stripe_webhook_secret ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700")}>
              {settings.stripe_webhook_secret ? 'Configured' : 'Not Set'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Email Settings Section
const EmailSettingsSection = ({ api }) => {
  var settingsState = useState({
    smtp_enabled: false,
    smtp_provider: 'custom',
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'DME R\'US',
    smtp_encryption: 'tls',
    // Provider-specific
    sendgrid_api_key: '',
    resend_api_key: ''
  });
  var settings = settingsState[0];
  var setSettings = settingsState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  var showPasswordState = useState(false);
  var showPassword = showPasswordState[0];
  var setShowPassword = showPasswordState[1];
  var testingState = useState(false);
  var testing = testingState[0];
  var setTesting = testingState[1];
  var testEmailState = useState('');
  var testEmail = testEmailState[0];
  var setTestEmail = testEmailState[1];

  useEffect(function() {
    api.get('/api/settings/email').then(function(res) {
      if (res.data) {
        setSettings({
          smtp_enabled: res.data.smtp_enabled || false,
          smtp_provider: res.data.smtp_provider || 'custom',
          smtp_host: res.data.smtp_host || '',
          smtp_port: res.data.smtp_port || '587',
          smtp_username: res.data.smtp_username || '',
          smtp_password: res.data.smtp_password || '',
          smtp_from_email: res.data.smtp_from_email || '',
          smtp_from_name: res.data.smtp_from_name || 'DME R\'US',
          smtp_encryption: res.data.smtp_encryption || 'tls',
          sendgrid_api_key: res.data.sendgrid_api_key || '',
          resend_api_key: res.data.resend_api_key || ''
        });
      }
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }, []);

  function handleChange(field, value) {
    var updated = {};
    for (var key in settings) { updated[key] = settings[key]; }
    updated[field] = value;
    setSettings(updated);
  }

  function handleSave() {
    setSaving(true);
    api.post('/api/settings/email', settings).then(function() {
      toast.success('Email settings saved');
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to save settings');
      setSaving(false);
    });
  }

  function handleTestEmail() {
    if (!testEmail) {
      toast.error('Enter a test email address');
      return;
    }
    setTesting(true);
    api.post('/api/settings/email/test', { to_email: testEmail }).then(function() {
      toast.success('Test email sent! Check your inbox.');
      setTesting(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
      setTesting(false);
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Email Settings</h2>
          <p className="text-gray-500 text-sm mt-1">Configure SMTP for sending emails (quotes, invoices, notifications)</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#014DB7]">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      {/* Enable Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Enable Email Sending</h3>
              <p className="text-sm text-gray-500">Send quotes, invoices, and notifications via email</p>
            </div>
          </div>
          <Switch
            checked={settings.smtp_enabled}
            onCheckedChange={function(checked) { handleChange('smtp_enabled', checked); }}
          />
        </div>
      </div>

      {/* Provider Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">Email Provider</h3>
          <p className="text-sm text-gray-500">Choose your email service provider</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'custom', label: 'Custom SMTP', desc: 'Any SMTP server' },
            { id: 'sendgrid', label: 'SendGrid', desc: 'sendgrid.com' },
            { id: 'resend', label: 'Resend', desc: 'resend.com' }
          ].map(function(provider) {
            return (
              <button
                key={provider.id}
                onClick={function() { handleChange('smtp_provider', provider.id); }}
                className={"p-4 rounded-xl border-2 text-left transition-all " + (
                  settings.smtp_provider === provider.id
                    ? 'border-[#014DB7] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <p className="font-medium text-gray-900">{provider.label}</p>
                <p className="text-xs text-gray-500">{provider.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom SMTP Settings */}
      {settings.smtp_provider === 'custom' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">SMTP Configuration</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">SMTP Host</Label>
              <Input
                value={settings.smtp_host}
                onChange={function(e) { handleChange('smtp_host', e.target.value); }}
                placeholder="smtp.gmail.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700">Port</Label>
              <Select value={settings.smtp_port} onValueChange={function(v) { handleChange('smtp_port', v); }}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 (Unencrypted)</SelectItem>
                  <SelectItem value="465">465 (SSL)</SelectItem>
                  <SelectItem value="587">587 (TLS)</SelectItem>
                  <SelectItem value="2525">2525 (Alternative)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">Username</Label>
              <Input
                value={settings.smtp_username}
                onChange={function(e) { handleChange('smtp_username', e.target.value); }}
                placeholder="your-email@domain.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700">Password / App Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={settings.smtp_password}
                  onChange={function(e) { handleChange('smtp_password', e.target.value); }}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button 
                  type="button"
                  onClick={function() { setShowPassword(!showPassword); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-gray-700">Encryption</Label>
            <Select value={settings.smtp_encryption} onValueChange={function(v) { handleChange('smtp_encryption', v); }}>
              <SelectTrigger className="mt-1 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="ssl">SSL</SelectItem>
                <SelectItem value="tls">TLS (Recommended)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* SendGrid Settings */}
      {settings.smtp_provider === 'sendgrid' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">SendGrid API Key</h3>
            <p className="text-sm text-gray-500">Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-[#014DB7] hover:underline">SendGrid Dashboard</a></p>
          </div>
          <div>
            <Label className="text-gray-700">API Key</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={settings.sendgrid_api_key}
                onChange={function(e) { handleChange('sendgrid_api_key', e.target.value); }}
                placeholder="SG.xxxxx..."
                className="font-mono text-sm pr-10"
              />
              <button 
                type="button"
                onClick={function() { setShowPassword(!showPassword); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Settings */}
      {settings.smtp_provider === 'resend' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Resend API Key</h3>
            <p className="text-sm text-gray-500">Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#014DB7] hover:underline">Resend Dashboard</a></p>
          </div>
          <div>
            <Label className="text-gray-700">API Key</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={settings.resend_api_key}
                onChange={function(e) { handleChange('resend_api_key', e.target.value); }}
                placeholder="re_xxxxx..."
                className="font-mono text-sm pr-10"
              />
              <button 
                type="button"
                onClick={function() { setShowPassword(!showPassword); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* From Address */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Sender Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-700">From Email</Label>
            <Input
              type="email"
              value={settings.smtp_from_email}
              onChange={function(e) { handleChange('smtp_from_email', e.target.value); }}
              placeholder="no-reply@yourdomain.com"
              className="mt-1"
            />
            <p className="text-xs text-gray-400 mt-1">Must be verified with your email provider</p>
          </div>
          <div>
            <Label className="text-gray-700">From Name</Label>
            <Input
              value={settings.smtp_from_name}
              onChange={function(e) { handleChange('smtp_from_name', e.target.value); }}
              placeholder="DME R'US"
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Test Configuration</h3>
        <div className="flex gap-3">
          <Input
            type="email"
            value={testEmail}
            onChange={function(e) { setTestEmail(e.target.value); }}
            placeholder="test@example.com"
            className="flex-1"
          />
          <Button 
            onClick={handleTestEmail} 
            disabled={testing || !settings.smtp_enabled}
            variant="outline"
          >
            {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Test Email
          </Button>
        </div>
        {!settings.smtp_enabled && (
          <p className="text-sm text-amber-600">Enable email sending above to test</p>
        )}
      </div>

      {/* Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Email Enabled</span>
            <span className={"px-2 py-1 rounded text-xs font-medium " + (settings.smtp_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600")}>
              {settings.smtp_enabled ? 'Active' : 'Disabled'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Provider</span>
            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
              {settings.smtp_provider === 'custom' ? 'Custom SMTP' : settings.smtp_provider === 'sendgrid' ? 'SendGrid' : 'Resend'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">From Email</span>
            <span className={"px-2 py-1 rounded text-xs font-medium " + (settings.smtp_from_email ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700")}>
              {settings.smtp_from_email || 'Not Set'}
            </span>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">Common SMTP Settings</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Gmail:</strong> smtp.gmail.com, Port 587, TLS (use App Password)</p>
          <p><strong>Outlook:</strong> smtp.office365.com, Port 587, TLS</p>
          <p><strong>Amazon SES:</strong> email-smtp.us-east-1.amazonaws.com, Port 587, TLS</p>
        </div>
      </div>
    </div>
  );
};

// Contract Documents Section
const ContractDocumentsSection = ({ api }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    is_default: false
  });

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const res = await api.get('/api/contract-templates');
      setContracts(res.data.templates || []);
    } catch (error) {
      console.log('No contracts found');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Contract name is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Contract content is required');
      return;
    }

    setSaving(true);
    try {
      if (editingContract) {
        await api.put('/api/contract-templates/' + editingContract.id, formData);
        toast.success('Contract updated');
      } else {
        await api.post('/api/contract-templates', formData);
        toast.success('Contract created');
      }
      await fetchContracts();
      resetForm();
    } catch (error) {
      toast.error('Failed to save contract');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    try {
      await api.delete('/api/contract-templates/' + contractId);
      toast.success('Contract deleted');
      await fetchContracts();
    } catch (error) {
      toast.error('Failed to delete contract');
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      name: contract.name,
      content: contract.content,
      is_default: contract.is_default || false
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingContract(null);
    setFormData({ name: '', content: '', is_default: false });
    setShowForm(false);
  };

  const MERGE_FIELDS = [
    { field: '{{client_name}}', description: 'Client full name' },
    { field: '{{company_name}}', description: 'Client company name' },
    { field: '{{quote_name}}', description: 'Quote/project name' },
    { field: '{{quote_total}}', description: 'Total quote amount' },
    { field: '{{date}}', description: 'Current date' },
    { field: '{{valid_until}}', description: 'Quote expiration date' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Contract Documents</h2>
          <p className="text-gray-500">Create and manage contract templates for quotes</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-[#014DB7] text-white rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> New Contract
          </Button>
        )}
      </div>

      {/* Contract Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#0A1628]">
              {editingContract ? 'Edit Contract' : 'New Contract'}
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contract Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Service Agreement"
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
              <Label className="cursor-pointer">Set as Default Contract</Label>
            </div>
          </div>

          <div>
            <Label>Contract Content *</Label>
            <div className="mt-1">
              <ContractEditor
                content={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
                placeholder="Enter contract terms and conditions..."
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Use merge fields to personalize contracts. Click to insert:
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {MERGE_FIELDS.map((mf) => (
                <button
                  key={mf.field}
                  type="button"
                  onClick={() => setFormData({ ...formData, content: formData.content + ' ' + mf.field })}
                  className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded-md transition-colors border border-blue-200"
                  title={mf.description}
                >
                  {mf.field}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#014DB7]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Contract'}
            </Button>
          </div>
        </div>
      )}

      {/* Contracts List */}
      {!showForm && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading contracts...
            </div>
          ) : contracts.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <ScrollText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">No Contract Documents</h3>
              <p className="text-gray-500 mb-4">Create your first contract template to attach to quotes</p>
              <Button onClick={() => setShowForm(true)} className="bg-[#014DB7]">
                <Plus className="w-4 h-4 mr-2" /> Create Contract
              </Button>
            </div>
          ) : (
            contracts.map((contract) => (
              <div key={contract.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#0A1628]">{contract.name}</h3>
                      {contract.is_default && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Default</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {contract.content?.substring(0, 200)}...
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Created: {new Date(contract.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(contract)}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(contract.id)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Merge Fields Reference */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">Available Merge Fields</h4>
        <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
          {MERGE_FIELDS.map((mf) => (
            <div key={mf.field}>
              <code className="bg-blue-100 px-1 rounded">{mf.field}</code> - {mf.description}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Site Rules Section
const SiteRulesSection = ({ api }) => {
  const [rules, setRules] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('terms');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const res = await api.get('/api/settings/rules');
        setRules(res.data || []);
      } catch (error) {
        console.log('No rules found');
      }
    };
    fetchRules();
  }, []);

  useEffect(() => {
    const rule = rules.find(r => r.category === selectedCategory);
    setContent(rule?.content || '');
  }, [selectedCategory, rules]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/settings/rules', { category: selectedCategory, content });
      setRules(prev => {
        const existing = prev.findIndex(r => r.category === selectedCategory);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { category: selectedCategory, content };
          return updated;
        }
        return [...prev, { category: selectedCategory, content }];
      });
      toast.success('Rule saved');
    } catch (error) {
      toast.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Site Rules & Policies</h2>
          <p className="text-gray-500">Manage terms, policies and legal content</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#014DB7] text-white rounded-xl">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Category Sidebar */}
        <div className="w-64 space-y-2">
          {RULE_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`w-full px-4 py-3 rounded-xl text-left transition-all flex items-center justify-between ${
                selectedCategory === cat.id 
                  ? 'bg-[#014DB7] text-white' 
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="font-medium">{cat.name}</span>
              {rules.find(r => r.category === cat.id) && (
                <Check className={`w-4 h-4 ${selectedCategory === cat.id ? 'text-white' : 'text-green-500'}`} />
              )}
            </button>
          ))}
        </div>

        {/* Content Editor */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 p-6">
          <Label className="text-lg font-semibold text-[#0A1628] mb-4 block">
            {RULE_CATEGORIES.find(c => c.id === selectedCategory)?.name}
          </Label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={`Enter your ${RULE_CATEGORIES.find(c => c.id === selectedCategory)?.name.toLowerCase()} content here...`}
            className="min-h-[400px] rounded-xl font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );
};

// Page Generator Section
const PageGeneratorSection = ({ api }) => {
  const [pages, setPages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const res = await api.get('/api/settings/pages');
        setPages(res.data || []);
      } catch (error) {
        console.log('No pages found');
      }
    };
    fetchPages();
  }, []);

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      await api.post('/api/settings/pages/generate-all');
      const res = await api.get('/api/settings/pages');
      setPages(res.data || []);
      toast.success(`Generated ${US_STATES.length} state pages`);
    } catch (error) {
      toast.error('Failed to generate pages');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSelected = async () => {
    if (selectedStates.length === 0) {
      toast.error('Select at least one state');
      return;
    }
    setGenerating(true);
    try {
      await api.post('/api/settings/pages/generate', { states: selectedStates });
      const res = await api.get('/api/settings/pages');
      setPages(res.data || []);
      toast.success(`Generated ${selectedStates.length} pages`);
      setSelectedStates([]);
    } catch (error) {
      toast.error('Failed to generate pages');
    } finally {
      setGenerating(false);
    }
  };

  const toggleState = (state) => {
    setSelectedStates(prev => 
      prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]
    );
  };

  const generatedStates = pages.map(p => p.state);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">SEO Landing Page Generator</h2>
          <p className="text-gray-500">Generate state-specific landing pages for SEO</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleGenerateSelected} 
            disabled={generating || selectedStates.length === 0}
            variant="outline"
            className="rounded-xl"
          >
            Generate Selected ({selectedStates.length})
          </Button>
          <Button 
            onClick={handleGenerateAll} 
            disabled={generating}
            className="bg-[#014DB7] text-white rounded-xl"
          >
            <Globe className="w-4 h-4 mr-2" /> {generating ? 'Generating...' : 'Generate All 50 States'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#0A1628]">State Pages ({pages.length}/50 generated)</h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-[#84CC16] rounded-full"></span> Generated
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-gray-200 rounded-full"></span> Not Generated
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-5 gap-2">
          {US_STATES.map(state => {
            const isGenerated = generatedStates.includes(state);
            const isSelected = selectedStates.includes(state);
            return (
              <button
                key={state}
                onClick={() => !isGenerated && toggleState(state)}
                disabled={isGenerated}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isGenerated 
                    ? 'bg-[#84CC16]/10 text-[#84CC16] border border-[#84CC16]/30 cursor-default'
                    : isSelected
                      ? 'bg-[#014DB7] text-white'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {state}
                {isGenerated && <Check className="w-3 h-3 inline ml-1" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Location Pages Section - One-Click Location Page Generator
const LocationPagesSection = ({ api }) => {
  const [states, setStates] = useState([]);
  const [stats, setStats] = useState({ states: 0, counties: 0, cities: 0, generated_pages: 0 });
  const [generatedPages, setGeneratedPages] = useState([]);
  const [generating, setGenerating] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState(null);
  const [stateData, setStateData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statesRes, statsRes, pagesRes] = await Promise.all([
        api.get('/api/dev/us-states'),
        api.get('/api/dev/stats'),
        api.get('/api/dev/generated-pages-grouped')
      ]);
      setStates(statesRes.data || []);
      setStats(statsRes.data || {});
      setGeneratedPages(pagesRes.data?.states || []);
    } catch (error) {
      console.error('Error fetching location data:', error);
      toast.error('Failed to load location data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerateState = async (stateSlug, stateName) => {
    setGenerating(prev => ({ ...prev, [stateSlug]: true }));
    try {
      const res = await api.post(`/api/dev/generate-state/${stateSlug}`, {
        include_counties: true,
        include_cities: true
      });
      toast.success(`Generated ${res.data.generated} pages for ${stateName}`);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error('Error generating pages:', error);
      toast.error(`Failed to generate pages for ${stateName}`);
    } finally {
      setGenerating(prev => ({ ...prev, [stateSlug]: false }));
    }
  };

  const handleGenerateAll = async () => {
    const ungenerated = states.filter(s => !generatedPages.find(g => g.slug === s.slug));
    if (ungenerated.length === 0) {
      toast.info('All states already generated');
      return;
    }
    
    for (const state of ungenerated.slice(0, 5)) { // Generate 5 at a time to avoid timeout
      await handleGenerateState(state.slug, state.name);
    }
    
    if (ungenerated.length > 5) {
      toast.info(`Generated 5 states. ${ungenerated.length - 5} remaining. Click again to continue.`);
    }
  };

  const handleDeleteStatePages = async (stateSlug) => {
    if (!window.confirm(`Delete all generated pages for this state?`)) return;
    try {
      await api.delete(`/api/dev/generated-pages/bulk/state/${stateSlug}`);
      toast.success('Pages deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pages');
    }
  };

  const handleViewState = async (stateSlug) => {
    try {
      const res = await api.get(`/api/dev/location-data/${stateSlug}`);
      setStateData(res.data);
      setSelectedState(stateSlug);
    } catch (error) {
      toast.error('Failed to load state data');
    }
  };

  const getGeneratedCount = (stateSlug) => {
    const found = generatedPages.find(g => g.slug === stateSlug);
    return found ? found.total_pages : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Location Page Generator</h2>
          <p className="text-gray-500">One-click SEO location pages with counties and cities</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/dev/location-preview`, '_blank')}
            variant="outline"
            className="rounded-xl border-[#014DB7] text-[#014DB7] hover:bg-[#014DB7]/5"
          >
            <Eye className="w-4 h-4 mr-2" /> Preview Sample Page
          </Button>
          <Button 
            onClick={handleGenerateAll}
            className="bg-[#84CC16] hover:bg-[#65a30d] text-white rounded-xl"
          >
            <Globe className="w-4 h-4 mr-2" /> Generate All States
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#014DB7]/10 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#014DB7]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">{stats.states}</p>
              <p className="text-xs text-gray-500">States</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">{stats.counties?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Counties</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">{stats.cities?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Cities</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#84CC16]/10 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#84CC16]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">{stats.generated_pages?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Generated Pages</p>
            </div>
          </div>
        </div>
      </div>

      {/* State Grid */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#0A1628]">US States</h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-[#84CC16] rounded-full"></span> Generated
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-gray-200 rounded-full"></span> Not Generated
            </span>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-3">
          {states.map(state => {
            const generatedCount = getGeneratedCount(state.slug);
            const isGenerated = generatedCount > 0;
            const isGenerating = generating[state.slug];
            
            return (
              <div
                key={state.slug}
                className={`relative p-3 rounded-xl border transition-all ${
                  isGenerated 
                    ? 'bg-[#84CC16]/5 border-[#84CC16]/30' 
                    : 'bg-gray-50 border-gray-200 hover:border-[#014DB7]/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-[#0A1628] truncate">{state.name}</span>
                  {isGenerated && <Check className="w-4 h-4 text-[#84CC16] flex-shrink-0" />}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {state.county_count} counties • {state.city_count} cities
                </div>
                {isGenerated ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#84CC16] font-medium">{generatedCount} pages</span>
                    <button 
                      onClick={() => handleViewState(state.slug)}
                      className="ml-auto p-1 text-gray-400 hover:text-[#014DB7]"
                      title="View details"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <button 
                      onClick={() => handleDeleteStatePages(state.slug)}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="Delete pages"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleGenerateState(state.slug, state.name)}
                    disabled={isGenerating}
                    className="w-full h-7 text-xs bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-lg"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>Generate</>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* State Detail Modal */}
      {selectedState && stateData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-[#0A1628]">{stateData.name}</h2>
                <p className="text-sm text-gray-500">{stateData.counties?.length || 0} counties • {stateData.cities?.length || 0} cities</p>
              </div>
              <button onClick={() => { setSelectedState(null); setStateData(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-[#0A1628] mb-2">Counties ({stateData.counties?.length || 0})</h4>
                  <div className="flex flex-wrap gap-2">
                    {stateData.counties?.slice(0, 30).map(county => (
                      <span key={county} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-lg">
                        {county}
                      </span>
                    ))}
                    {stateData.counties?.length > 30 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-lg">
                        +{stateData.counties.length - 30} more
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0A1628] mb-2">Cities ({stateData.cities?.length || 0})</h4>
                  <div className="flex flex-wrap gap-2">
                    {stateData.cities?.slice(0, 50).map(city => (
                      <span key={city} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">
                        {city}
                      </span>
                    ))}
                    {stateData.cities?.length > 50 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-lg">
                        +{stateData.cities.length - 50} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-between">
              <a 
                href={`/api/locations/dme-compliant-software-${stateData.slug}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#014DB7] hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" /> View Generated Page
              </a>
              <Button onClick={() => { setSelectedState(null); setStateData(null); }} variant="outline" className="rounded-xl">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Directory Submissions Section
const DIRECTORIES = [
  { id: 'google_business', name: 'Google Business Profile', url: 'https://business.google.com', icon: '🔍' },
  { id: 'yelp', name: 'Yelp Business', url: 'https://biz.yelp.com', icon: '⭐' },
  { id: 'bing_places', name: 'Bing Places', url: 'https://www.bingplaces.com', icon: '🔎' },
  { id: 'apple_maps', name: 'Apple Maps Connect', url: 'https://mapsconnect.apple.com', icon: '🍎' },
  { id: 'yellowpages', name: 'Yellow Pages', url: 'https://www.yellowpages.com', icon: '📒' },
  { id: 'bbb', name: 'Better Business Bureau', url: 'https://www.bbb.org', icon: '🏆' },
  { id: 'healthgrades', name: 'Healthgrades', url: 'https://www.healthgrades.com', icon: '🏥' },
  { id: 'zocdoc', name: 'Zocdoc', url: 'https://www.zocdoc.com', icon: '👨‍⚕️' },
  { id: 'vitals', name: 'Vitals', url: 'https://www.vitals.com', icon: '💊' },
  { id: 'webmd', name: 'WebMD Directory', url: 'https://doctor.webmd.com', icon: '🩺' },
  { id: 'linkedin', name: 'LinkedIn Company', url: 'https://www.linkedin.com/company', icon: '💼' },
  { id: 'facebook', name: 'Facebook Business', url: 'https://www.facebook.com/business', icon: '📘' },
];

const DirectorySubmissionsSection = ({ api }) => {
  const [directorySubmissions, setDirectorySubmissions] = useState({});
  const [customDirectories, setCustomDirectories] = useState([]);
  const [newDirectory, setNewDirectory] = useState({ name: '', url: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/api/settings/directories');
        setDirectorySubmissions(res.data?.submissions || {});
        setCustomDirectories(res.data?.custom || []);
      } catch (e) {
        console.log('No directory data yet');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleToggleDirectory = async (directoryId, submitted) => {
    const newSubmissions = { ...directorySubmissions, [directoryId]: submitted ? new Date().toISOString() : null };
    setDirectorySubmissions(newSubmissions);
    
    try {
      await api.post('/api/settings/directories', { submissions: newSubmissions, custom: customDirectories });
      toast.success(submitted ? 'Marked as submitted' : 'Marked as not submitted');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleAddCustomDirectory = async () => {
    if (!newDirectory.name || !newDirectory.url) {
      toast.error('Please fill in both name and URL');
      return;
    }
    
    const newCustom = [...customDirectories, { 
      id: `custom_${Date.now()}`, 
      name: newDirectory.name, 
      url: newDirectory.url,
      icon: '🔗'
    }];
    setCustomDirectories(newCustom);
    setNewDirectory({ name: '', url: '' });
    
    try {
      await api.post('/api/settings/directories', { submissions: directorySubmissions, custom: newCustom });
      toast.success('Directory added');
    } catch (error) {
      toast.error('Failed to add directory');
    }
  };

  const handleRemoveCustomDirectory = async (directoryId) => {
    const newCustom = customDirectories.filter(d => d.id !== directoryId);
    setCustomDirectories(newCustom);
    
    const newSubmissions = { ...directorySubmissions };
    delete newSubmissions[directoryId];
    setDirectorySubmissions(newSubmissions);
    
    try {
      await api.post('/api/settings/directories', { submissions: newSubmissions, custom: newCustom });
      toast.success('Directory removed');
    } catch (error) {
      toast.error('Failed to remove directory');
    }
  };

  const allDirectories = [...DIRECTORIES, ...customDirectories];
  const submittedCount = Object.values(directorySubmissions).filter(Boolean).length;
  const totalCount = allDirectories.length;
  const progressPercent = totalCount > 0 ? (submittedCount / totalCount) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Directory Submissions</h2>
          <p className="text-gray-500">Track business listing submissions for SEO</p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-[#84CC16]">{submittedCount}</span>
          <span className="text-gray-400"> / {totalCount} submitted</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Submission Progress</span>
          <span className="text-sm font-bold text-[#84CC16]">{Math.round(progressPercent)}%</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#84CC16] to-[#65a30d] transition-all duration-500 rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Directory Grid */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-[#0A1628] mb-4">Business Directories</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allDirectories.map(dir => {
            const isSubmitted = !!directorySubmissions[dir.id];
            const submittedDate = directorySubmissions[dir.id];
            const isCustom = dir.id.startsWith('custom_');
            
            return (
              <div 
                key={dir.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  isSubmitted 
                    ? 'border-[#84CC16] bg-[#84CC16]/5' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{dir.icon}</span>
                    <div>
                      <p className="font-medium text-[#0A1628]">{dir.name}</p>
                      <a 
                        href={dir.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[#014DB7] hover:underline flex items-center gap-1"
                      >
                        Visit <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCustom && (
                      <button
                        onClick={() => handleRemoveCustomDirectory(dir.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove directory"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleDirectory(dir.id, !isSubmitted)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        isSubmitted 
                          ? 'bg-[#84CC16] text-white' 
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      title={isSubmitted ? 'Mark as not submitted' : 'Mark as submitted'}
                    >
                      {isSubmitted ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {isSubmitted && submittedDate && (
                  <p className="text-xs text-gray-500 mt-2 pl-11">
                    Submitted: {new Date(submittedDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Custom Directory */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-[#0A1628] mb-4">Add Custom Directory</h3>
        <div className="flex gap-3">
          <Input
            placeholder="Directory Name"
            value={newDirectory.name}
            onChange={(e) => setNewDirectory({ ...newDirectory, name: e.target.value })}
            className="rounded-xl flex-1"
          />
          <Input
            placeholder="https://..."
            value={newDirectory.url}
            onChange={(e) => setNewDirectory({ ...newDirectory, url: e.target.value })}
            className="rounded-xl flex-1"
          />
          <Button 
            onClick={handleAddCustomDirectory}
            className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
};

// SEO Tools Section
const SEOToolsSection = ({ api }) => {
  const [submitting, setSubmitting] = useState({});
  const [sitemapUrl, setSitemapUrl] = useState('');

  useEffect(() => {
    setSitemapUrl(`${window.location.origin}/sitemap.xml`);
  }, []);

  const handleSubmit = async (engine) => {
    setSubmitting(prev => ({ ...prev, [engine]: true }));
    try {
      await api.post('/api/settings/seo/submit-sitemap', { engine, sitemap_url: sitemapUrl });
      toast.success(`Sitemap submitted to ${engine}`);
    } catch (error) {
      toast.error(`Failed to submit to ${engine}`);
    } finally {
      setSubmitting(prev => ({ ...prev, [engine]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#0A1628]">SEO Tools</h2>
        <p className="text-gray-500">Submit your sitemap to search engines</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Sitemap URL</Label>
            <div className="flex gap-3">
              <Input value={sitemapUrl} onChange={(e) => setSitemapUrl(e.target.value)} className="rounded-xl flex-1" />
              <Button variant="outline" className="rounded-xl" onClick={() => window.open(sitemapUrl, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" /> View
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Google */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-[#0A1628]">Google Search Console</h3>
              <p className="text-sm text-gray-500">Submit to Google</p>
            </div>
          </div>
          <Button 
            onClick={() => handleSubmit('google')} 
            disabled={submitting.google}
            className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white rounded-xl"
          >
            {submitting.google ? 'Submitting...' : 'Submit to Google'}
          </Button>
        </div>

        {/* Bing */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#008373" d="M5 3v16.5l4.5 2.5 8-4.5V13l-6 3.5-2.5-1.5V7l-4-2z"/>
                <path fill="#00A78E" d="M9.5 7v8l6 3.5V14l-6-3.5z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-[#0A1628]">Bing Webmaster Tools</h3>
              <p className="text-sm text-gray-500">Submit to Bing</p>
            </div>
          </div>
          <Button 
            onClick={() => handleSubmit('bing')} 
            disabled={submitting.bing}
            className="w-full bg-[#008373] hover:bg-[#006B5F] text-white rounded-xl"
          >
            {submitting.bing ? 'Submitting...' : 'Submit to Bing'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Products Section
const ProductsSection = ({ api }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, catsRes] = await Promise.all([
          api.get('/api/settings/products'),
          api.get('/api/settings/product-categories'),
        ]);
        setProducts(productsRes.data || []);
        setCategories(catsRes.data || []);
      } catch (error) {
        console.log('No products found');
      }
    };
    fetchData();
  }, []);

  const handleSaveProduct = async (product) => {
    try {
      if (editingProduct) {
        await api.put(`/api/settings/products/${editingProduct.id}`, product);
        toast.success('Product updated');
      } else {
        await api.post('/api/settings/products', product);
        toast.success('Product created');
      }
      const res = await api.get('/api/settings/products');
      setProducts(res.data || []);
      setModalOpen(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/api/settings/products/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">Product Catalog</h2>
          <p className="text-gray-500">Manage your products and pricing</p>
        </div>
        <Button onClick={() => { setEditingProduct(null); setModalOpen(true); }} className="bg-[#014DB7] text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {products.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Product</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Category</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Price</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map(product => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0A1628]">{product.name}</p>
                    {product.description && <p className="text-xs text-gray-500">{product.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{product.category}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#0A1628]">${product.price?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${product.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {product.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setEditingProduct(product); setModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-[#014DB7]">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteProduct(product.id)} className="p-1.5 text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">No products yet</p>
          <Button onClick={() => setModalOpen(true)} className="bg-[#014DB7] text-white rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> Add First Product
          </Button>
        </div>
      )}

      {/* Product Modal */}
      {modalOpen && (
        <ProductModal 
          product={editingProduct}
          categories={categories}
          onSave={handleSaveProduct}
          onClose={() => { setModalOpen(false); setEditingProduct(null); }}
        />
      )}
    </div>
  );
};

const ProductModal = ({ product, categories, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || '',
    price: product?.price || '',
    sku: product?.sku || '',
    active: product?.active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, price: parseFloat(formData.price) || 0 });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#0A1628]">{product ? 'Edit Product' : 'Add Product'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label>Product Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="rounded-xl" placeholder="e.g., DME Equipment" />
            </div>
            <div className="space-y-2">
              <Label>Price ($) *</Label>
              <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required className="rounded-xl" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label>Active</Label>
            <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({...formData, active: checked})} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button type="submit" className="bg-[#014DB7] text-white rounded-xl">{product ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Database Section
const DatabaseSection = ({ api }) => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleExport = async (collection) => {
    setExporting(true);
    try {
      const res = await api.get(`/api/settings/database/export?collection=${collection}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collection}_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success(`Exported ${collection}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post('/api/settings/database/import', data);
      toast.success('Data imported successfully');
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const collections = ['leads', 'projects', 'tasks', 'users', 'invoices', 'products'];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#0A1628]">Database Management</h2>
        <p className="text-gray-500">Export and import your data</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Export */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-[#014DB7]" /> Export Data
          </h3>
          <p className="text-sm text-gray-500 mb-4">Download your data as JSON files</p>
          <div className="space-y-2">
            {collections.map(col => (
              <Button 
                key={col}
                variant="outline" 
                className="w-full justify-start rounded-xl"
                onClick={() => handleExport(col)}
                disabled={exporting}
              >
                <Download className="w-4 h-4 mr-2" />
                Export {col.charAt(0).toUpperCase() + col.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Import */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#84CC16]" /> Import Data
          </h3>
          <p className="text-sm text-gray-500 mb-4">Restore data from a JSON backup file</p>
          <label className="block">
            <input 
              type="file" 
              accept=".json"
              onChange={handleImport}
              className="hidden"
              disabled={importing}
            />
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#014DB7] hover:bg-[#014DB7]/5 transition-all">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">
                {importing ? 'Importing...' : 'Click to upload JSON file'}
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50 rounded-xl border border-red-200 p-6">
        <h3 className="font-semibold text-red-700 mb-2">Danger Zone</h3>
        <p className="text-sm text-red-600 mb-4">These actions are irreversible. Please be careful.</p>
        <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100 rounded-xl">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset Database
        </Button>
      </div>
    </div>
  );
};

// Future Section Placeholder
const FutureSection = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center h-[400px] bg-gray-50 rounded-xl border border-gray-200">
    <div className="w-16 h-16 bg-gray-200 rounded-2xl flex items-center justify-center mb-4">
      <Clock className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-600 mb-2">{title}</h3>
    <p className="text-gray-400">Coming soon</p>
  </div>
);

// Main Component
export const SuperAdminPage = () => {
  const { user, api } = useAuth();
  const [activeSection, setActiveSection] = useState('general');

  // Only super admins can access this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center h-[400px]">
        <X className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Access Denied</h2>
        <p className="text-gray-500">You need Super Admin privileges to access this page.</p>
      </div>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'general': return <GeneralSettings api={api} />;
      case 'features': return <FeaturesSection api={api} />;
      case 'email': return <EmailSettingsSection api={api} />;
      case 'payments': return <PaymentSettingsSection api={api} />;
      case 'gateway-logs': return <GatewayLogsSection api={api} />;
      case 'rules': return <SiteRulesSection api={api} />;
      case 'pages': return <PageGeneratorSection api={api} />;
      case 'locations': return <LocationPagesSection api={api} />;
      case 'directories': return <DirectorySubmissionsSection api={api} />;
      case 'seo': return <SEOToolsSection api={api} />;
      case 'products': return <ProductsSection api={api} />;
      case 'database': return <DatabaseSection api={api} />;
      case 'api': return <FutureSection title="API Tools" description="API key management" />;
      case 'logs': return <FutureSection title="System Logs" description="Activity logs" />;
      default: return <GeneralSettings api={api} />;
    }
  };

  return (
    <div className="flex gap-6 -m-6 p-6 bg-[#F8FAFC] min-h-[calc(100vh-64px)]">
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sticky top-6">
          <h2 className="text-lg font-bold text-[#0A1628] px-4 mb-4">Super Admin</h2>
          <div className="space-y-1">
            {MENU_ITEMS.map(item => (
              <SidebarMenuItem
                key={item.id}
                item={item}
                active={activeSection === item.id}
                onClick={() => !item.future && setActiveSection(item.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {renderSection()}
      </div>
    </div>
  );
};

export default SuperAdminPage;
